import { useHttp, useTranslate } from '@/shared/lib'
import { useNotification } from '@/shared/ui'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import type { FilterCondition } from '@/widgets/GlobalSearch/model/type.ts'
import { stripFilterLocalKeys } from '@/widgets/GlobalSearch/model/helpers.ts'

export type SearchPresetSaveInput = {
  name: string
  items: FilterCondition[][]
  /** Если задан — обновление существующего пресета (PUT), иначе создание (POST). */
  id?: number | null
}

/**
 * Функция сохранения пресета
 */
export const useSearchPresetSave = () => {
  const http = useHttp()
  const { addError, addSuccess } = useNotification()
  const { trs } = useTranslate()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: ({ id, name, items }: SearchPresetSaveInput) => {
      const body = { name, items }
      if (id) {
        return http.put(`/search/presets/${id}`, body)
      }
      return http.post(`/search/presets`, body)
    },
  })

  /**
   * Сохраняет пресет. Бросает ошибку при неудаче — чтобы confirm/модалка
   * могли оставить диалог открытым.
   */
  const save = async (input: SearchPresetSaveInput) => {
    try {
      await mutation.mutateAsync({
        id: input.id ?? null,
        name: input.name,
        items: stripFilterLocalKeys(input.items),
      })

      addSuccess(trs('itf.saved_preset'))

      // Не ждём refetch: иначе модалка «висит», если GET /presets задерживается.
      void queryClient.invalidateQueries({ queryKey: ['load_search_presrts'] })
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
      throw error
    }
  }

  return {
    ...mutation,

    save,
  }
}
